import { createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode } from 'react'
import { loadPaper, loadSuno } from './data'
import type { PaperWithSong, SunoClip } from './types'

interface PlayerState {
  currentPaperId: string | null
  isPlaying: boolean
  isLoading: boolean
  currentTime: number
  duration: number
  volume: number
  showLyrics: boolean
  paper: PaperWithSong | null
  suno: SunoClip | null
}

interface PlayerContextValue extends PlayerState {
  play: (paperId: string) => Promise<void>
  pause: () => void
  toggle: () => void
  seek: (time: number) => void
  setVolume: (volume: number) => void
  toggleLyrics: () => void
  closeLyrics: () => void
  audioRef: React.RefObject<HTMLAudioElement | null>
}

const PlayerContext = createContext<PlayerContextValue | null>(null)

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [state, setState] = useState<PlayerState>({
    currentPaperId: null,
    isPlaying: false,
    isLoading: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    showLyrics: false,
    paper: null,
    suno: null,
  })

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => {
      setState(s => ({ ...s, currentTime: audio.currentTime }))
    }

    const handleDurationChange = () => {
      setState(s => ({ ...s, duration: audio.duration || 0 }))
    }

    const handleEnded = () => {
      setState(s => ({ ...s, isPlaying: false }))
    }

    const handleWaiting = () => {
      setState(s => ({ ...s, isLoading: true }))
    }

    const handlePlaying = () => {
      setState(s => ({ ...s, isLoading: false, isPlaying: true }))
    }

    const handlePause = () => {
      setState(s => ({ ...s, isPlaying: false }))
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('playing', handlePlaying)
    audio.addEventListener('pause', handlePause)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('waiting', handleWaiting)
      audio.removeEventListener('playing', handlePlaying)
      audio.removeEventListener('pause', handlePause)
    }
  }, [])

  // Sync volume to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = state.volume
    }
  }, [state.volume])

  const play = useCallback(async (paperId: string) => {
    // If same paper, just resume
    if (paperId === state.currentPaperId && audioRef.current) {
      audioRef.current.play()
      return
    }

    // Load new paper - clear old data immediately
    setState(s => ({ ...s, isLoading: true, currentPaperId: paperId, paper: null, suno: null }))

    try {
      const [paper, suno] = await Promise.all([
        loadPaper(paperId),
        loadSuno(paperId),
      ])

      if (!paper || !suno?.audio_url) {
        setState(s => ({ ...s, isLoading: false }))
        return
      }

      setState(s => ({
        ...s,
        paper,
        suno,
        isLoading: false,
        currentTime: 0,
        duration: suno.duration || 0,
      }))

      // Update audio source and play
      if (audioRef.current) {
        audioRef.current.src = suno.audio_url
        audioRef.current.load()
        audioRef.current.play()
      }
    } catch (error) {
      console.error('Failed to load paper:', error)
      setState(s => ({ ...s, isLoading: false }))
    }
  }, [state.currentPaperId])

  const pause = useCallback(() => {
    audioRef.current?.pause()
  }, [])

  const toggle = useCallback(() => {
    if (!audioRef.current) return
    if (state.isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
  }, [state.isPlaying])

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time
    }
  }, [])

  const setVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume))
    setState(s => ({ ...s, volume: clampedVolume }))
  }, [])

  const toggleLyrics = useCallback(() => {
    setState(s => ({ ...s, showLyrics: !s.showLyrics }))
  }, [])

  const closeLyrics = useCallback(() => {
    setState(s => ({ ...s, showLyrics: false }))
  }, [])

  return (
    <PlayerContext.Provider
      value={{
        ...state,
        play,
        pause,
        toggle,
        seek,
        setVolume,
        toggleLyrics,
        closeLyrics,
        audioRef,
      }}
    >
      {children}
      {/* Hidden audio element */}
      <audio ref={audioRef} preload="metadata" />
    </PlayerContext.Provider>
  )
}

export function usePlayer() {
  const context = useContext(PlayerContext)
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider')
  }
  return context
}
